import { X } from 'lucide-react';
import type { ReactElement, ReactNode } from 'react';

import { Table, type TableColumn } from '@/_shared/components/Table';
import { Button } from '@/_shared/components/ui/button';
import { Callout } from '@/_shared/components/ui/callout';
import { Skeleton } from '@/_shared/components/ui/skeleton';
import { cn } from '@/_shared/lib/utils';

import {
  NO_COLUMN_METADATA_COPY,
  PREVIEW_EMPTY_COPY,
  PREVIEW_TIMED_OUT_TITLE,
  previewHeaderText,
  previewTimedOutMessage,
} from '../lib/audience-preview';

type PreviewRow = Record<string, unknown>;

export interface AudiencePreviewOk {
  status: 'ok';
  columns: { name: string }[];
  rows: PreviewRow[];
  rowLimit: number;
  totalCount: number | null;
}

export type AudiencePreviewUnavailable =
  | { status: 'unavailable'; reason: 'timed_out'; timeoutMs: number }
  | { status: 'unavailable'; reason: 'no_column_metadata' };

export function AudiencePreviewPanel({
  sourceName,
  loading,
  error,
  stale,
  onRefresh,
  onClose,
  result,
  fillHeight = false,
}: {
  sourceName: string;
  loading: boolean;
  error: string | null;
  stale: boolean;
  onRefresh: () => void;
  onClose: () => void;
  result: AudiencePreviewOk | AudiencePreviewUnavailable | null;
  /** Fill the parent pane (bottom split) instead of a capped inline table. */
  fillHeight?: boolean;
}): ReactElement {
  const columns: TableColumn<PreviewRow>[] =
    result?.status === 'ok'
      ? result.columns.map((column) => ({
          id: column.name,
          header: column.name,
        }))
      : [];

  let body: ReactNode;
  if (loading) {
    body = (
      <div className="grid gap-2" role="status" aria-label="Loading preview">
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-8 w-3/4" />
      </div>
    );
  } else if (error) {
    body = (
      <Callout variant="error" role="alert">
        {error}
      </Callout>
    );
  } else if (result?.status === 'unavailable') {
    body =
      result.reason === 'timed_out' ? (
        <Callout variant="warning">
          <p className="font-medium">{PREVIEW_TIMED_OUT_TITLE}</p>
          <p className="mt-1">{previewTimedOutMessage(result.timeoutMs)}</p>
        </Callout>
      ) : (
        <Callout variant="warning">
          <p className="font-medium">Preview isn't available for this table</p>
          <p className="mt-1">{NO_COLUMN_METADATA_COPY}</p>
        </Callout>
      );
  } else if (result?.status === 'ok') {
    body = (
      <Table
        data={result.rows}
        columns={columns}
        getRowId={(_row, index) => String(index)}
        emptyState={PREVIEW_EMPTY_COPY}
        maxHeight="20rem"
        fillHeight={fillHeight}
        bordered
      />
    );
  } else {
    body = null;
  }

  // A timeout is worth retrying — a suspended warehouse is often awake on the second try.
  const timedOut = result?.status === 'unavailable' && result.reason === 'timed_out';

  const header =
    !loading && !error && result?.status === 'ok'
      ? previewHeaderText(sourceName, result.rows.length, result.totalCount, result.rowLimit)
      : 'Preview';

  return (
    <section
      id="audience-preview-panel"
      aria-label="Audience preview"
      className={cn('flex min-h-0 flex-col', fillHeight ? 'h-full px-6 py-4' : 'mt-5')}
    >
      <div className="mb-2 flex shrink-0 items-center justify-between gap-2">
        <h3 className="min-w-0 truncate text-sm font-semibold text-foreground">{header}</h3>
        <div className="flex shrink-0 items-center gap-1">
          {(stale || error || timedOut) && !loading ? (
            <Button variant="tertiary" size="sm" label="Refresh" onClick={onRefresh} />
          ) : null}
          <Button
            type="button"
            variant="tertiary"
            size="sm"
            iconOnly
            aria-label="Close preview"
            Icon={<X />}
            onClick={onClose}
          />
        </div>
      </div>
      {stale && !loading ? (
        <Callout variant="info" className="mb-3 shrink-0">
          The definition changed since this sample ran. Refresh to preview the current conditions.
        </Callout>
      ) : null}
      <div className={cn(fillHeight && 'min-h-0 flex-1')}>{body}</div>
    </section>
  );
}
